import React, { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";

type Suggestion = {
  place_id: number;
  display_name: string;
  lat: string;       // string from API
  lon: string;       // string from API
  boundingbox?: [string, string, string, string]; // [south, north, west, east] or sometimes [minlat,maxlat,minlon,maxlon]
  type?: string;
  class?: string;
};

interface GeoSearchControlProps {
  /** Called when a place is chosen. Handy to clear polygon. */
  onPlaceSelected?: (params: { lat: number; lon: number }) => void;
  /** Whether to clear drawn polygon on selection (default true). */
  clearOnSelect?: boolean;
  /** Optional country code filter (comma-separated, e.g., "lb,sy,jo") */
  countryCodes?: string;
}

/** Simple in-map search box that pans/zooms to selected place using Nominatim. */
const GeoSearchControl: React.FC<GeoSearchControlProps> = ({
  onPlaceSelected,
  countryCodes,
}) => {
  const map = useMap();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // basic debounce
  const debouncedQuery = useDebounce(query, 250);

  useEffect(() => {
    const controller = new AbortController();
    async function run() {
      const q = debouncedQuery.trim();
      if (!q) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          format: "json",
          addressdetails: "1",
          q,
          limit: "8",
        });
        if (countryCodes) params.set("countrycodes", countryCodes);

        const { data: json } = await axios.get<Suggestion[]>(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
          headers: {
            "Accept-Language": navigator.language || "en",
          },
          signal: controller.signal,
        });
        setSuggestions(json);
        setOpen(true);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }
    run();
    return () => controller.abort();
  }, [debouncedQuery, countryCodes]);

  // close on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (s: Suggestion) => {
    const lat = parseFloat(s.lat);
    const lon = parseFloat(s.lon);

    // Prefer bbox fit if available; fallback to flyTo
    const bb = normalizeBoundingBox(s.boundingbox);
    if (bb) {
      const bounds = L.latLngBounds([bb.south, bb.west], [bb.north, bb.east]);
      map.fitBounds(bounds, { padding: [24, 24] });
    } else {
      map.flyTo([lat, lon], Math.max(map.getZoom(), 10), { duration: 0.75 });
    }

    onPlaceSelected?.({ lat, lon });
    setOpen(false);
  };

  return (
    <div
      ref={boxRef}
      className="absolute z-[1000] top-3 left-3"
      style={{ minWidth: 280 }}
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-200 flex items-center">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search country, city… (e.g., Lebanon, Beirut)"
          className="px-3 py-2 w-full rounded-xl outline-none text-sm"
        />
        {loading ? (
          <span className="pr-3 text-xs text-gray-500">…</span>
        ) : query ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="pr-3 text-xs text-gray-500 hover:text-gray-700"
            aria-label="Clear"
          >
            ✕
          </button>
        ) : (
          <span className="pr-3 text-gray-400">🔎</span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className="mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-72 overflow-auto">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
              onClick={() => choose(s)}
            >
              <div className="font-medium text-gray-800 truncate">
                {primaryName(s.display_name)}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {secondaryName(s.display_name)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeoSearchControl;

/* ---------------- helpers ---------------- */

function primaryName(display: string) {
  // Take the first segment as main name
  return display.split(",")[0]?.trim() ?? display;
}
function secondaryName(display: string) {
  const parts = display.split(",");
  return parts.slice(1).join(",").trim();
}

/** Handle variant orders from Nominatim and return {south,north,west,east} or null */
function normalizeBoundingBox(
  bbox?: [string, string, string, string]
): { south: number; north: number; west: number; east: number } | null {
  if (!bbox || bbox.length !== 4) return null;

  // Nominatim docs say: [south, north, west, east], but some wrappers return [minlat, maxlat, minlon, maxlon]
  // We'll detect by ranges.
  const a = bbox.map(parseFloat);
  if (Number.isNaN(a[0]) || Number.isNaN(a[1]) || Number.isNaN(a[2]) || Number.isNaN(a[3])) return null;

  const [v0, v1, v2, v3] = a;
  // If v0..v1 look like lat range and v2..v3 like lon range, assume [south, north, west, east]
  const looksLikeSNWE = Math.abs(v0) <= 90 && Math.abs(v1) <= 90 && Math.abs(v2) <= 180 && Math.abs(v3) <= 180;
  if (looksLikeSNWE) {
    return { south: Math.min(v0, v1), north: Math.max(v0, v1), west: Math.min(v2, v3), east: Math.max(v2, v3) };
  }
  // Fallback: try [minlat, maxlat, minlon, maxlon]
  const looksLikeMinMax = Math.abs(v0) <= 90 && Math.abs(v1) <= 90 && Math.abs(v2) <= 180 && Math.abs(v3) <= 180;
  if (looksLikeMinMax) {
    return { south: Math.min(v0, v1), north: Math.max(v0, v1), west: Math.min(v2, v3), east: Math.max(v2, v3) };
  }
  return null;
}

function useDebounce<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
